import { createAsyncThunk, createSlice } from "@reduxjs/toolkit"
import DireccionService from "common/services/DireccionService"
import EmpresaService from "common/services/EmpresaService"
import FormasContactoService from "common/services/FromasContactoService"
import notification from "common/utils/notification"
import { FORMAS_DE_CONTACTO } from "features/Contactos/constants"
import UserService from "features/User/services/UserService"
import i18next from "i18next"
import { nanoid } from "nanoid"
import { mapCurrentUser } from "../../utils"
import { getEmpresaActivaId, setUserInfo } from "./userSlice"

const empresaService = new EmpresaService()
const userService = new UserService()

export const refreshEmpresaData = createAsyncThunk(
  "empresa/refreshData",
  async (_, { dispatch }) => {
    const { data } = await userService.getUserByToken()
    const userData = mapCurrentUser(data)
    dispatch(setUserInfo(userData))
  }
)

export const fetchDireccionesEmpresa = createAsyncThunk(
  "empresa/fetchDirecciones",
  async (_, thunkAPI) => {
    const id = getEmpresaActivaId(thunkAPI.getState())
    const { data } = await DireccionService.getDireccionEmpresaById(id)
    return data
  }
)

export const createDireccionEmpresa = createAsyncThunk(
  "empresa/createDireccion",
  async (direccion, thunkAPI) => {
    await DireccionService.insertDireccion(direccion)

    notification.success({
      body: `${i18next.t("notification.directionCorrectlyCreated")}`,
    })
    thunkAPI.dispatch(refreshEmpresaData())
    thunkAPI.dispatch(fetchDireccionesEmpresa())
  }
)

export const updateDireccionEmpresa = createAsyncThunk(
  "empresa/updateDireccion",
  async (direccion, thunkAPI) => {
    await DireccionService.updateDireccion(direccion)
    notification.success({
      body: `${i18next.t("notification.directionCorrectlyUpdate")}`,
    })

    thunkAPI.dispatch(refreshEmpresaData())
    thunkAPI.dispatch(fetchDireccionesEmpresa())
  }
)

export const deleteDireccionEmpresa = createAsyncThunk(
  "empresa/deleteDireccion",
  async (direccion, { dispatch }) => {
    // @ts-expect-error TS(2339): Property 'nuevo' does not exist on type 'void'.
    if (!direccion.nuevo) {
      // @ts-expect-error TS(2339): Property 'id' does not exist on type 'void'.
      await DireccionService.deleteDireccion(direccion.id)

      notification.success({
        body: `${i18next.t("notification.directionCorrectlyDelete")}`,
      })

      dispatch(refreshEmpresaData())
      dispatch(fetchDireccionesEmpresa())
    }

    // @ts-expect-error TS(2339): Property 'id' does not exist on type 'void'.
    return direccion.id
  }
)

export const fetchFormasContactoEmpresa = createAsyncThunk(
  "empresa/fetchFormasContacto",
  async (_, thunkAPI) => {
    const id = getEmpresaActivaId(thunkAPI.getState())
    const { data } = await FormasContactoService.getFormasContactoEmpresaById(
      id
    )
    return data
  }
)

export const deleteFormaContactoEmpresa = createAsyncThunk(
  "empresa/deleteFormaContacto",
  async (contacto, { dispatch }) => {
    // @ts-expect-error TS(2339): Property 'nuevo' does not exist on type 'void'.
    if (!contacto.nuevo) {
      // @ts-expect-error TS(2339): Property 'id' does not exist on type 'void'.
      await FormasContactoService.deleteFormaContacto(contacto.id)

      notification.success({
        body: `${i18next.t("notification.contactCorrectDelete")}`,
      })

      dispatch(refreshEmpresaData())
      dispatch(fetchFormasContactoEmpresa())
    }

    // @ts-expect-error TS(2339): Property 'id' does not exist on type 'void'.
    return contacto.id
  }
)

export const createFormaContactoEmpresa = createAsyncThunk(
  "empresa/createFormaContacto",
  async (contacto, { dispatch }) => {
    const { data } = await FormasContactoService.insertFormaContacto(contacto)

    notification.success({
      body: `${i18next.t("notification.contactCorrectCreated")}`,
    })

    dispatch(refreshEmpresaData())
    dispatch(fetchFormasContactoEmpresa())
    return data
  }
)

export const updateFormaContactoEmpresa = createAsyncThunk(
  "empresa/updateFormaContacto",
  async (contacto, { dispatch }) => {
    await FormasContactoService.updateFormaContacto(contacto)

    notification.success({
      body: `${i18next.t("notification.contactCorrectUpdate")}`,
    })

    dispatch(refreshEmpresaData())
    dispatch(fetchFormasContactoEmpresa())
  }
)

export const updateActividadEmpresa = createAsyncThunk(
  "empresa/updateActividad",
  async (empresa, { getState }) => {
    const id = getEmpresaActivaId(getState())
    await empresaService.update({ empresa, id })
    notification.success({
      body: `${i18next.t("notification.companyCorrectUpdate")}`,
    })
  }
)

const initialState = {
  loadingDireccion: false,
  newDireccionFormActive: false,
  newContactoFormActive: false,
  contactos: [],
  direcciones: [],
  showValidarEmpresaModal: false,
  showValidarEmpresaErrorModal: { show: false, info: null },
  showValidarEmpresaLoadingModal: false,
}

export const empresaSlice = createSlice({
  name: "empresa",
  initialState,
  reducers: {
    addEmptyDireccion(state) {
      if (state.newDireccionFormActive) return

      // @ts-expect-error TS(2345): Argument of type 'never' is not assignable to par...
      state.direcciones?.push({
        id: nanoid(),
        calle: "",
        codigoPostal: null,
        poblacionId: null,
        principal: false,
        provinciaNombre: "",
        poblacionNombre: "",
        nuevo: true,
      })
      state.newDireccionFormActive = true
    },
    addEmptyContacto(state) {
      if (state.newContactoFormActive) return

      // @ts-expect-error TS(2345): Argument of type 'never' is not assignable to par...
      state.contactos.push({
        id: nanoid(),
        contacto: "",
        nombre: "",
        tipoFormaContacto: FORMAS_DE_CONTACTO.EMAIL,
        nuevo: true,
        principal: false,
      })
      state.newContactoFormActive = true
    },
    setShowValidarEmpresaModal(state) {
      state.showValidarEmpresaModal = true
    },
    setShowValidarEmpresaErrorModal(state, { payload }) {
      state.showValidarEmpresaErrorModal = {
        show: true,
        info: payload,
      }
    },
    setHideValidarEmpresaModal(state) {
      state.showValidarEmpresaModal = false
    },

    // LOADING MODAL
    setShowValidarEmpresaLoadingModal(state) {
      state.showValidarEmpresaLoadingModal = true
    },
    setHideValidarEmpresaLoadingModal(state) {
      state.showValidarEmpresaLoadingModal = false
    },

    // ERROR MODAL
    settHideValidarEmpresaErrorModal(state) {
      state.showValidarEmpresaErrorModal = {
        show: false,
        info: null,
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchDireccionesEmpresa.pending, (state: $TSMigrationFixMe) => {
        state.direcciones = null
      })
      .addCase(fetchDireccionesEmpresa.fulfilled, (state, { payload }) => {
        state.direcciones = payload
        state.newDireccionFormActive = false
      })
      .addCase(
        fetchFormasContactoEmpresa.pending,
        (state: $TSMigrationFixMe) => {
          state.contactos = null
        }
      )
      .addCase(fetchFormasContactoEmpresa.fulfilled, (state, { payload }) => {
        state.contactos = payload
        state.newContactoFormActive = false
      })
      .addCase(deleteDireccionEmpresa.fulfilled, (state, { payload: id }) => {
        state.direcciones = state.direcciones?.filter(
          (direccion: $TSMigrationFixMe) => direccion.id !== id
        )
        state.newDireccionFormActive = false
      })
      .addCase(
        deleteFormaContactoEmpresa.fulfilled,
        (state, { payload: id }) => {
          state.contactos = state.contactos?.filter(
            (contacto: $TSMigrationFixMe) => contacto.id !== id
          )
          state.newContactoFormActive = false
        }
      )
      .addCase(updateDireccionEmpresa.pending, (state) => {
        state.loadingDireccion = true
      })
      .addCase(updateDireccionEmpresa.rejected, (state) => {
        state.loadingDireccion = false
      })
      .addCase(updateDireccionEmpresa.fulfilled, (state) => {
        state.loadingDireccion = false
      })
      .addCase(createDireccionEmpresa.pending, (state) => {
        state.loadingDireccion = true
      })
      .addCase(createDireccionEmpresa.rejected, (state) => {
        state.loadingDireccion = false
      })
      .addCase(createDireccionEmpresa.fulfilled, (state) => {
        state.loadingDireccion = false
      })
  },
})

export const {
  addEmptyDireccion,
  addEmptyContacto,
  setShowValidarEmpresaModal,
  // LOADING MODAL
  setShowValidarEmpresaLoadingModal,
  setHideValidarEmpresaLoadingModal,
  // ERROR MODAL
  setShowValidarEmpresaErrorModal,
  settHideValidarEmpresaErrorModal,

  setHideValidarEmpresaModal,
} = empresaSlice.actions

export const empresaSelector = (state: $TSMigrationFixMe) => state.empresa
